import { useEffect, useMemo, useState } from "react";
import { loadCatalogIndex, type CatalogIndex, type CatalogItemView } from "../../../core/data/local/catalog";
import { entryUnit, formatEntry, parseEntry, QuantityError } from "../quantity";
import type { Ingredient, InventoryApi, Recipe } from "./api";

/**
 * Recipe authoring (T021, FR-067, contracts/inventory-api.md "Recipes").
 *
 * Pick a drink, then a size ("every size" by default), then what the recipe
 * is for — the drink itself, or one add-on. Resolution adds an add-on's
 * recipe on top of the drink's, so an extra shot is just "+9 g beans", and a
 * substitute gives back what it replaces: oat milk is "+200 ml oat, −200 ml
 * milk".
 *
 * Quantities are typed in the unit people think in — millilitres of a litre
 * ingredient, grams of a kilogram one — which is exactly the stored integer.
 *
 * Add-ons are per-drink copies in this catalog, so one saved for "Extra shot"
 * can be applied to every drink carrying an add-on of that name in one go.
 */

interface DraftLine {
  key: string;
  ingredientId: string;
  quantity: string;
}

let draftKey = 0;
const nextKey = () => `line-${++draftKey}`;

function itemGroups(index: CatalogIndex): Array<{ label: string; items: CatalogItemView[] }> {
  return index.tabs.map((tab) => ({
    label: tab.name,
    items: [tab, ...(index.sectionsByTab.get(tab.id) ?? [])].flatMap(
      (category) => index.itemsByCategory.get(category.id) ?? [],
    ),
  }));
}

export interface RecipeEditorProps {
  api: InventoryApi;
  initialItemId: string | null;
}

export function RecipeEditor({ api, initialItemId }: RecipeEditorProps) {
  const [catalog, setCatalog] = useState<CatalogIndex | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [itemId, setItemId] = useState<string>(initialItemId ?? "");
  const [variantId, setVariantId] = useState<string>("");
  const [modifierId, setModifierId] = useState<string>("");
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [applyToAll, setApplyToAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadCatalogIndex().then(setCatalog);
    void api
      .ingredients()
      .then((body) => setIngredients(body.ingredients))
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : "Could not load ingredients"));
  }, [api]);

  const item = itemId ? catalog?.itemsById.get(itemId) ?? null : null;
  const modifiers = useMemo(() => item?.modifierGroups.flatMap((group) => group.modifiers) ?? [], [item]);
  const modifierName = modifiers.find((modifier) => modifier.id === modifierId)?.name ?? null;
  const byId = useMemo(() => new Map(ingredients.map((ingredient) => [ingredient.id, ingredient])), [ingredients]);

  // Load this drink's recipes whenever the drink changes.
  useEffect(() => {
    setRecipes([]);
    if (!itemId) return;
    let cancelled = false;
    void api
      .recipes(itemId)
      .then((body) => !cancelled && setRecipes(body.recipes))
      .catch((caught: unknown) => !cancelled && setError(caught instanceof Error ? caught.message : "Could not load recipes"));
    return () => {
      cancelled = true;
    };
  }, [api, itemId]);

  const current =
    recipes.find(
      (recipe) =>
        (recipe.variantId ?? "") === variantId && (recipe.modifierId ?? "") === modifierId,
    ) ?? null;

  // Reset the draft to whatever is saved for the chosen drink / size / add-on.
  useEffect(() => {
    setLines(
      (current?.lines ?? []).map((line) => {
        const ingredient = byId.get(line.ingredientId);
        return {
          key: nextKey(),
          ingredientId: line.ingredientId,
          quantity: ingredient ? formatEntry(line.quantity, ingredient.unit).split(" ")[0]! : String(line.quantity),
        };
      }),
    );
    setApplyToAll(false);
  }, [current, byId]);

  const describe = (recipe: Recipe): string => {
    const size = recipe.variantId ? catalog?.variantsById.get(recipe.variantId)?.name ?? "one size" : "Every size";
    const addOn = recipe.modifierId ? catalog?.modifiersById.get(recipe.modifierId)?.name ?? "an add-on" : null;
    return addOn ? `${size} + ${addOn}` : size;
  };

  const save = async (asEmpty = false) => {
    if (!item) return;
    setError(null);
    setNotice(null);

    const payload: Array<{ ingredientId: string; quantity: number }> = [];
    if (!asEmpty) {
      for (const line of lines) {
        const ingredient = byId.get(line.ingredientId);
        if (!ingredient) {
          setError("Choose an ingredient on every line, or remove the empty one.");
          return;
        }
        try {
          payload.push({
            ingredientId: ingredient.id,
            quantity: parseEntry(line.quantity, ingredient.unit, { allowNegative: modifierId !== "" }),
          });
        } catch (caught) {
          setError(
            `${ingredient.name}: ${caught instanceof QuantityError ? caught.message : "that is not a quantity."}`,
          );
          return;
        }
      }
    }

    setBusy(true);
    try {
      const body = await api.saveRecipe({
        menuItemId: item.item.id,
        variantId: variantId || null,
        modifierId: modifierId || null,
        lines: payload,
        ...(applyToAll && modifierId ? { applyToSameNamedModifiers: true } : {}),
      });
      setRecipes((await api.recipes(item.item.id)).recipes);
      setNotice(
        body.recipes.length > 1
          ? `Saved for ${body.recipes.length} drinks with “${modifierName}”.`
          : asEmpty
            ? `${item.item.name} saved as using nothing we track.`
            : `${item.item.name} saved. Sales from now on deduct it.`,
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!current || !item) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteRecipe(current.id);
      setRecipes((await api.recipes(item.item.id)).recipes);
      setNotice("Recipe removed. Past sales keep what they deducted.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not remove it");
    } finally {
      setBusy(false);
    }
  };

  if (!catalog) return <p className="inv__empty">Loading the menu…</p>;

  return (
    <section className="inv__panel inv__form" aria-label="Recipes">
      <div className="inv__pickers">
        <label className="inv__field">
          <span>Drink or item</span>
          <select
            value={itemId}
            onChange={(event) => {
              setItemId(event.target.value);
              setVariantId("");
              setModifierId("");
              setNotice(null);
            }}
          >
            <option value="">Choose…</option>
            {itemGroups(catalog).map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map((view) => (
                  <option key={view.item.id} value={view.item.id}>
                    {view.item.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        {item && item.variants.length > 1 && (
          <label className="inv__field">
            <span>Size</span>
            <select value={variantId} onChange={(event) => setVariantId(event.target.value)}>
              <option value="">Every size</option>
              {item.variants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {item && modifiers.length > 0 && (
          <label className="inv__field">
            <span>Recipe for</span>
            <select value={modifierId} onChange={(event) => setModifierId(event.target.value)}>
              <option value="">The drink itself</option>
              {modifiers.map((modifier) => (
                <option key={modifier.id} value={modifier.id}>
                  + {modifier.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {item && recipes.length > 0 && (
        <p className="inv__hint">Saved for {item.item.name}: {recipes.map(describe).join(" · ")}</p>
      )}

      {item && (
        <>
          <p className="inv__hint">
            {modifierId
              ? `What “${modifierName}” adds to one ${item.item.name}. Use a minus for what it replaces.`
              : variantId
                ? "What one of this size uses. Sizes without their own recipe use “Every size”."
                : "What one uses, whatever the size — unless a size has its own."}
          </p>

          {ingredients.length === 0 ? (
            <p className="inv__empty">Add ingredients on the Ingredients tab first.</p>
          ) : (
            <ul className="inv__lines">
              {lines.map((line) => {
                const ingredient = byId.get(line.ingredientId);
                return (
                  <li key={line.key} className="inv__line">
                    <select
                      value={line.ingredientId}
                      aria-label="Ingredient"
                      onChange={(event) =>
                        setLines((all) =>
                          all.map((l) => (l.key === line.key ? { ...l, ingredientId: event.target.value } : l)),
                        )
                      }
                    >
                      <option value="">Ingredient…</option>
                      {ingredients.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <input
                      value={line.quantity}
                      aria-label="Quantity"
                      inputMode="decimal"
                      onChange={(event) =>
                        setLines((all) =>
                          all.map((l) => (l.key === line.key ? { ...l, quantity: event.target.value } : l)),
                        )
                      }
                    />
                    <span className="inv__line-unit">{ingredient ? entryUnit(ingredient.unit) : ""}</span>
                    <button
                      type="button"
                      className="inv__action"
                      aria-label="Remove line"
                      onClick={() => setLines((all) => all.filter((l) => l.key !== line.key))}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            className="inv__action"
            onClick={() => setLines((all) => [...all, { key: nextKey(), ingredientId: "", quantity: "" }])}
            disabled={ingredients.length === 0}
          >
            + Ingredient
          </button>

          {modifierId && (
            <label className="inv__check">
              <input type="checkbox" checked={applyToAll} onChange={(event) => setApplyToAll(event.target.checked)} />
              <span>Use this for every drink with “{modifierName}”</span>
            </label>
          )}

          {error && (
            <p className="inv__error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="inv__notice" role="status">
              {notice}
            </p>
          )}

          <div className="inv__buttons">
            {current && (
              <button type="button" className="inv__action is-danger" onClick={() => void remove()} disabled={busy}>
                Remove recipe
              </button>
            )}
            {!modifierId && lines.length === 0 && (
              <button type="button" className="inv__action" onClick={() => void save(true)} disabled={busy}>
                Uses nothing we track
              </button>
            )}
            <button
              type="button"
              className="inv__action is-primary"
              onClick={() => void save()}
              disabled={busy || lines.length === 0}
            >
              Save recipe
            </button>
          </div>
        </>
      )}
    </section>
  );
}
